<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';
import { api, ApiCallError } from '@/api/client';
import { useCreateStore } from '@/stores/create';
import { useUserStore } from '@/stores/user';
import { haptic } from '@/composables/useTelegramWebApp';
import MxButton from '@/components/MxButton.vue';
import MxCard from '@/components/MxCard.vue';
import MxIcon from '@/components/MxIcon.vue';
import MxIconShelf from '@/components/MxIconShelf.vue';
import MxSegmented from '@/components/MxSegmented.vue';
import MxSheet from '@/components/MxSheet.vue';
import MxTextarea from '@/components/MxTextarea.vue';
import MxPromptChips from '@/components/MxPromptChips.vue';
import { findTemplate } from '@/lib/templates';
import { MODES } from '@/lib/modes';
import { promptSuggestionsFor, appendSuggestion, type PromptSuggestion } from '@/lib/prompt-suggestions';
import type { ImproveOut, ModelInfo, UploadOut } from '@/api/types';

const route = useRoute();
const router = useRouter();
const create = useCreateStore();
const userStore = useUserStore();
const { params, models, cost, canAfford, submitError, appliedTemplate } = storeToRefs(create);

const needFirstFrameHint = computed(
  () =>
    !!appliedTemplate.value &&
    appliedTemplate.value.hint_first_frame === 'required' &&
    !params.value.first_frame_url,
);

const MODEL_EMOJI: Record<string, string> = {
  seedance_2: '🚀',
  seedance_2_fast: '⚡',
  grok_imagine: '✨',
  wan_2_6_flash: '🌊',
  veo3_fast: '🎬',
  veo3: '🎥',
  kling_3_motion: '🎭',
  gpt_image_2: '🖼',
  nano_banana_2: '🍌',
};

const modeShelf = MODES.map((m) => ({ id: m.kind, icon: m.icon, label: m.label }));

const modelShelf = computed(() =>
  models.value.map((m) => ({
    id: m.key,
    emoji: MODEL_EMOJI[m.key] ?? '🎬',
    label: m.label,
  })),
);

const sheetAspect = ref(false);
const sheetDuration = ref(false);
const improving = ref(false);

const promptSuggestions = promptSuggestionsFor('video');
function applySuggestion(s: PromptSuggestion) {
  params.value.prompt = appendSuggestion(params.value.prompt, s.text);
  void create.estimate();
}

onMounted(async () => {
  create.reset('video');
  await create.loadModels('video');

  const tplId = route.query.template;
  const modelKey = route.query.model;
  if (typeof tplId === 'string') {
    const t = findTemplate(tplId);
    if (t && t.kind === 'video') create.applyTemplate(t);
  } else if (typeof modelKey === 'string') {
    const m = create.models.find((x) => x.key === modelKey);
    if (m) await pickModel(m);
  }
  await create.estimate();
  try { localStorage.setItem('last_create_kind', 'video'); } catch { /* */ }
});

const validation = computed(() => create.validate());
const payDisabled = computed(() => validation.value !== null || cost.value === null || !canAfford.value);
const payLabel = computed(() => {
  if (validation.value) return validation.value;
  if (cost.value === null) return 'Считаю...';
  if (!canAfford.value) return `Не хватает ${cost.value} т.`;
  return `Сгенерировать · ~${cost.value} т.`;
});

async function pickModel(m: ModelInfo) {
  params.value.model_key = m.key;
  if (!m.resolutions.includes(params.value.resolution ?? '720p')) {
    params.value.resolution = (m.resolutions[0] ?? '720p') as any;
  }
  if (!m.available_durations.includes(params.value.duration ?? 10)) {
    params.value.duration = m.default_duration;
  }
  if (m.default_aspect_ratio && !m.available_aspect_ratios.includes(params.value.aspect_ratio ?? '9:16')) {
    params.value.aspect_ratio = (m.default_aspect_ratio as any);
  }
  await create.estimate();
}

async function uploadFile(file: File): Promise<UploadOut | null> {
  const fd = new FormData();
  fd.append('file', file);
  try {
    return await api.postMultipart<UploadOut>('/uploads', fd);
  } catch {
    return null;
  }
}

async function pickFrame(slot: 'first' | 'end') {
  const inp = document.createElement('input');
  inp.type = 'file';
  inp.accept = 'image/*';
  inp.onchange = async () => {
    const f = inp.files?.[0];
    if (!f) return;
    const out = await uploadFile(f);
    if (!out) return;
    if (slot === 'first') params.value.first_frame_url = out.url;
    else params.value.end_frame_url = out.url;
  };
  inp.click();
}

async function pickRef(kind: 'image' | 'video' | 'audio') {
  const inp = document.createElement('input');
  inp.type = 'file';
  inp.accept = `${kind}/*`;
  inp.multiple = true;
  inp.onchange = async () => {
    const files = Array.from(inp.files ?? []);
    for (const f of files) {
      const out = await uploadFile(f);
      if (!out) continue;
      const target =
        kind === 'image'
          ? 'reference_image_urls'
          : kind === 'video'
          ? 'reference_video_urls'
          : 'reference_audio_urls';
      params.value[target] = [...(params.value[target] ?? []), out.url];
    }
  };
  inp.click();
}

async function improvePrompt() {
  if (!params.value.prompt.trim()) return;
  improving.value = true;
  try {
    const out = await api.post<ImproveOut>('/prompt/improve', {
      prompt: params.value.prompt,
      model_key: params.value.model_key,
    });
    params.value.prompt = out.improved_prompt;
    haptic.notification('success');
  } catch (e) {
    if (e instanceof ApiCallError) submitError.value = e.message;
    haptic.notification('error');
  } finally {
    improving.value = false;
  }
}

async function pay() {
  haptic.impact('medium');
  const out = await create.submit();
  if (out) {
    await userStore.load();
    await router.push(`/generation/${out.id}`);
  } else {
    haptic.notification('error');
  }
}

function onSegmentChange(t: 'video' | 'image') {
  if (t === 'image') void router.push('/create/image');
}
</script>

<template>
  <section class="cv">
    <MxIconShelf
      :items="modeShelf"
      active-id="video"
      size="lg"
      @update:active-id="(id) => onSegmentChange(id as 'video' | 'image')"
    />

    <MxIconShelf
      :items="modelShelf"
      :active-id="params.model_key"
      @update:active-id="(id) => { const m = models.find(x => x.key === id); if (m) void pickModel(m); }"
    />

    <div class="prompt">
      <div class="prompt__hdr">
        <p class="row__lbl">Описание</p>
        <button class="improve" :disabled="improving || !params.prompt.trim()" @click="improvePrompt">
          <MxIcon name="Sparkles" :size="16" />
          <span>{{ improving ? 'Думаю...' : 'ИИ' }}</span>
        </button>
      </div>
      <MxTextarea v-model="params.prompt" placeholder="Что должно происходить в кадре" :min-rows="4" />
      <MxPromptChips :items="promptSuggestions" @pick="applySuggestion" />
    </div>

    <div class="grid2">
      <MxCard class="slot" clickable @click="pickFrame('first')">
        <p class="row__lbl">Первый кадр</p>
        <p class="slot__hint">{{ params.first_frame_url ? 'Заменить' : '+ Загрузить' }}</p>
      </MxCard>
      <MxCard class="slot" clickable @click="pickFrame('end')">
        <p class="row__lbl">Финальный кадр</p>
        <p class="slot__hint">{{ params.end_frame_url ? 'Заменить' : '+ Загрузить' }}</p>
      </MxCard>
    </div>

    <h3 class="sect">Референсы</h3>
    <div class="refs">
      <MxCard class="ref ref--lead" clickable @click="pickRef('image')">
        <MxIcon name="Image" />
        <span class="ref__lbl">Изображения</span>
        <span class="num ref__count">{{ params.reference_image_urls?.length ?? 0 }}</span>
      </MxCard>
      <MxCard class="ref" clickable @click="pickRef('video')">
        <MxIcon name="Film" />
        <span class="num ref__count">{{ params.reference_video_urls?.length ?? 0 }}</span>
      </MxCard>
      <MxCard class="ref" clickable @click="pickRef('audio')">
        <MxIcon name="Music" />
        <span class="num ref__count">{{ params.reference_audio_urls?.length ?? 0 }}</span>
      </MxCard>
    </div>

    <h3 class="sect">Параметры</h3>

    <div class="param-block" v-if="(create.activeModel?.resolutions ?? []).length > 1">
      <p class="row__lbl">Качество</p>
      <MxSegmented
        :model-value="params.resolution ?? (create.activeModel?.resolutions[0] ?? '720p')"
        :options="(create.activeModel?.resolutions ?? []).map(r => ({ value: r, label: r }))"
        @update:model-value="(v) => { params.resolution = v as any; void create.estimate(); }"
      />
    </div>

    <div class="param-block" v-if="(create.activeModel?.available_durations ?? []).length > 1">
      <p class="row__lbl">Длительность</p>
      <MxSegmented
        v-if="(create.activeModel?.available_durations ?? []).length <= 4"
        :model-value="String(params.duration ?? create.activeModel?.default_duration ?? 10)"
        :options="(create.activeModel?.available_durations ?? []).map(d => ({ value: String(d), label: `${d} сек` }))"
        @update:model-value="(v) => { params.duration = Number(v); void create.estimate(); }"
      />
      <MxCard v-else class="row" clickable @click="sheetDuration = true">
        <div class="row__main">
          <p class="row__val">{{ params.duration }} сек</p>
        </div>
        <MxIcon name="ChevronRight" />
      </MxCard>
    </div>

    <div class="param-block" v-if="(create.activeModel?.available_aspect_ratios ?? []).length > 1">
      <p class="row__lbl">Соотношение</p>
      <MxSegmented
        v-if="(create.activeModel?.available_aspect_ratios ?? []).length <= 4"
        :model-value="params.aspect_ratio ?? (create.activeModel?.available_aspect_ratios[0] ?? '9:16')"
        :options="(create.activeModel?.available_aspect_ratios ?? []).map(a => ({ value: a, label: a }))"
        @update:model-value="(v) => { params.aspect_ratio = v as any; void create.estimate(); }"
      />
      <MxCard v-else class="row" clickable @click="sheetAspect = true">
        <div class="row__main">
          <p class="row__val">{{ params.aspect_ratio }}</p>
        </div>
        <MxIcon name="ChevronRight" />
      </MxCard>
    </div>

    <div class="param-block" v-if="create.activeModel?.supports?.includes('audio')">
      <p class="row__lbl">Аудио</p>
      <MxSegmented
        :model-value="params.generate_audio ? 'on' : 'off'"
        :options="[{ value: 'off', label: 'Без звука' }, { value: 'on', label: 'Со звуком' }]"
        @update:model-value="(v) => { params.generate_audio = v === 'on'; void create.estimate(); }"
      />
    </div>

    <button
      v-if="needFirstFrameHint"
      class="tpl-hint"
      data-test="tpl-hint"
      @click="pickFrame('first')"
    >
      <MxIcon name="ImagePlus" :size="20" />
      <span class="tpl-hint__txt">
        Для шаблона «{{ appliedTemplate!.title }}» загрузите фото — оно станет первым кадром.
      </span>
      <MxIcon name="ChevronRight" :size="18" />
    </button>

    <!-- Validation message is mirrored on the disabled paybar button itself,
         so we surface it here only when it would otherwise be hidden — i.e.
         a refs/frame conflict that the button doesn't have room to spell out. -->
    <p v-if="submitError" class="alert">{{ submitError }}</p>

    <div class="paybar">
      <MxButton :disabled="payDisabled" @click="pay" variant="primary">{{ payLabel }}</MxButton>
    </div>

    <MxSheet v-model="sheetAspect" title="Соотношение сторон">
      <ul class="models">
        <li v-for="a in (create.activeModel?.available_aspect_ratios ?? [])" :key="a">
          <button
            :class="['model', { 'model--active': a === params.aspect_ratio }]"
            @click="params.aspect_ratio = a as any; sheetAspect = false; void create.estimate();"
          >
            <span class="model__lbl num">{{ a }}</span>
          </button>
        </li>
      </ul>
    </MxSheet>

    <MxSheet v-model="sheetDuration" title="Длительность">
      <ul class="models">
        <li v-for="d in (create.activeModel?.available_durations ?? [])" :key="d">
          <button
            :class="['model', { 'model--active': d === params.duration }]"
            @click="params.duration = d; sheetDuration = false; void create.estimate();"
          >
            <span class="model__lbl"><span class="num">{{ d }}</span> сек</span>
          </button>
        </li>
      </ul>
    </MxSheet>
  </section>
</template>

<style scoped>
.cv { padding: var(--sp-3) 0 120px; display: flex; flex-direction: column; gap: var(--sp-3); }
.row { display: flex; align-items: center; justify-content: space-between; }
.row__main { display: flex; flex-direction: column; gap: 4px; }
.row__lbl { font-size: var(--fs-sm); color: var(--text-secondary); }
.row__val { font-weight: 600; }
.prompt { display: flex; flex-direction: column; gap: var(--sp-2); }
.prompt__hdr { display: flex; justify-content: space-between; align-items: center; }
.improve {
  display: inline-flex; align-items: center; gap: 4px;
  color: var(--accent); font-weight: 600; padding: 6px 10px; border-radius: var(--r-sm);
  background: var(--accent-soft);
  transition: transform 0.12s ease, background 0.18s ease;
}
.improve:active:not(:disabled) { transform: scale(0.96); }
.improve:disabled { opacity: 0.4; }
.grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: var(--sp-3); }
.slot { display: flex; flex-direction: column; gap: 4px; }
.slot__hint { color: var(--accent); font-size: var(--fs-sm); font-weight: 500; }
.sect { font-size: var(--fs-sm); color: var(--text-secondary); margin-top: var(--sp-3); text-transform: uppercase; letter-spacing: 0.06em; font-weight: 500; }
.refs {
  display: grid;
  /* Asymmetric — image refs are the primary affordance, video/audio collapse to icons. */
  grid-template-columns: 1.7fr 1fr 1fr;
  gap: var(--sp-3);
}
.ref {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: var(--sp-3);
}
.ref--lead .ref__lbl {
  font-size: var(--fs-sm);
  color: var(--text-primary);
}
.ref__count {
  margin-left: auto;
  color: var(--text-secondary);
  font-size: var(--fs-sm);
  min-width: 1ch;
  text-align: right;
}
.param-block { display: flex; flex-direction: column; gap: var(--sp-2); }
.alert { color: var(--error); font-size: var(--fs-sm); padding: var(--sp-2); background: var(--bg-elevated); border-radius: var(--r-md); }
.alert--warn { color: var(--warning); }
.tpl-hint {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  width: 100%;
  text-align: left;
  padding: var(--sp-3);
  border-radius: var(--r-md);
  background:
    linear-gradient(135deg, var(--accent-soft) 0%, transparent 80%),
    var(--bg-elevated);
  border: 1px solid var(--accent-soft);
  color: var(--text-primary);
  -webkit-tap-highlight-color: transparent;
  transition: transform 0.12s ease, border-color 0.18s ease;
}
.tpl-hint:active { transform: scale(0.98); }
.tpl-hint__txt { flex: 1; font-size: var(--fs-sm); line-height: 1.35; }
.paybar {
  position: fixed; bottom: 64px; left: 50%; transform: translateX(-50%);
  width: 100%; max-width: 480px; padding: var(--sp-3) var(--sp-4) calc(var(--sp-3) + env(safe-area-inset-bottom));
  background: linear-gradient(transparent, var(--bg-base) 30%);
  z-index: 30;
}
.paybar .btn { width: 100%; }
.models { display: flex; flex-direction: column; gap: var(--sp-2); }
.model { display: flex; flex-direction: column; gap: 4px; padding: var(--sp-3); border-radius: var(--r-md); background: var(--bg-overlay); width: 100%; text-align: left; }
.model--active { border: 1px solid var(--accent); }
.model__lbl { font-weight: 600; }
.model__hint { color: var(--text-secondary); font-size: var(--fs-sm); }
</style>
